import React from 'react';
import type { PlayerStats } from '@/api/resultsApi';

interface PlayerCardProps {
  playerId: number;
  stats: PlayerStats;
  team?: 'A' | 'B';
  isHighlighted?: boolean;
}

/**
 * Card riassuntiva delle statistiche di un giocatore.
 * Mostra kills, aces, muri, difese, punti, tentativi ed errori.
 */
const PlayerCard: React.FC<PlayerCardProps> = ({ playerId, stats, team, isHighlighted = false }) => {
  const teamColor = team === 'A' ? '#3b82f6' : team === 'B' ? '#ef4444' : '#6b7280';

  const statItems: { label: string; value: number; key: keyof PlayerStats; color?: string }[] = [
    { label: 'Punti', value: stats.points, key: 'points', color: '#f59e0b' },
    { label: 'Kills', value: stats.kills, key: 'kills', color: '#22c55e' },
    { label: 'Aces', value: stats.aces, key: 'aces', color: '#06b6d4' },
    { label: 'Muri', value: stats.blocks, key: 'blocks', color: '#f97316' },
    { label: 'Difese', value: stats.digs, key: 'digs', color: '#6366f1' },
    { label: 'Errori', value: stats.errors, key: 'errors', color: '#ef4444' },
    { label: 'Att. Tent.', value: stats.attack_attempts, key: 'attack_attempts' },
    { label: 'Batt. Tent.', value: stats.serve_attempts, key: 'serve_attempts' },
  ];

  return (
    <div
      style={{
        border: `2px solid ${isHighlighted ? teamColor : '#e2e8f0'}`,
        borderRadius: '12px',
        padding: '1rem',
        backgroundColor: isHighlighted ? `${teamColor}08` : '#ffffff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        minWidth: '160px',
        transition: 'all 0.2s',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '0.75rem',
          borderBottom: '1px solid #e2e8f0',
          paddingBottom: '0.5rem',
        }}
      >
        <div
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            backgroundColor: teamColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '0.85rem',
          }}
        >
          {playerId}
        </div>
        <div>
          <div style={{ fontWeight: 600, fontSize: '0.95rem', color: '#1e293b' }}>
            Giocatore {playerId}
          </div>
          {team && (
            <div style={{ fontSize: '0.75rem', color: teamColor, fontWeight: 500 }}>
              Team {team}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem 0.75rem' }}>
        {statItems.map((item) => (
          <div key={item.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8rem' }}>
            <span style={{ color: '#64748b' }}>{item.label}</span>
            <span
              style={{
                fontWeight: 600,
                color: item.color || '#1e293b',
                minWidth: '24px',
                textAlign: 'right',
              }}
            >
              {item.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlayerCard;